"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import QuorumProposalDropModal from "./quorum/QuorumProposalDropModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { QueryKey, ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { useAuth } from "../auth/Auth";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant, IdentityPickerPlacement } from "./dropComposer.types";
import { DropMode } from "./dropComposer.types";
import { ChatRestriction, type DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { getMentionedGroupsFromParts } from "@/helpers/waves/drop-group-mentions";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onServerDropCreated?: ((drop: ApiDrop) => Promise<void> | void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
  readonly initialCurationUrl?: string | null | undefined;
  readonly onSubmitCurationUrl?: ((url: string) => void) | undefined;
  readonly canSubmitCurationUrl?: boolean | undefined;
  readonly curationUrlSubmitRestrictionMessage?: string | null | undefined;
  readonly externalAttachmentDrop?: {
    readonly token: number;
    readonly files: File[];
  } | null | undefined;
  readonly onExternalAttachmentDropConsumed?: (() => void) | undefined;
  readonly termsSignatureFlowEnabled?: boolean | undefined;
  readonly identityPickerPlacement?: IdentityPickerPlacement | undefined;
  readonly forceStandardDropComposer?: boolean | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
  readonly onSuccess?: (() => void) | undefined;
  readonly onError?: ((error: unknown) => void) | undefined;
}
const ANIMATION_DURATION = 0.3;
interface SlowModeChatReservation {
  readonly id: number;
  readonly waveId: string;
  readonly cooldownMs: number;
}
interface QueuedDropMutationBody extends DropMutationBody {
  readonly slowModeChatReservation?: SlowModeChatReservation | undefined;
}
interface SlowModeChatWaveState {
  pendingReservationId: number | null;
  cooldownUntil: number | null;
  cooldownMs: number | null;
}
export default function CreateDrop(t0) {
  const $ = _c(142);
  const {
    activeDrop,
    onCancelReplyQuote,
    onDropAddedToQueue,
    onAllDropsAdded,
    onServerDropCreated,
    onExitFixedDropMode,
    wave,
    dropId,
    fixedDropMode,
    privileges,
    curationComposerVariant: t1,
    initialCurationUrl: t2,
    onSubmitCurationUrl,
    canSubmitCurationUrl,
    curationUrlSubmitRestrictionMessage: t3,
    externalAttachmentDrop,
    onExternalAttachmentDropConsumed,
    termsSignatureFlowEnabled: t4,
    identityPickerPlacement: t5,
    forceStandardDropComposer: t6
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const initialCurationUrlProp = t2 === undefined ? null : t2;
  const curationUrlSubmitRestrictionMessage = t3 === undefined ? null : t3;
  const termsSignatureFlowEnabled = t4 === undefined ? true : t4;
  const identityPickerPlacement = t5 === undefined ? "modal" : t5;
  const forceStandardDropComposer = t6 === undefined ? false : t6;
  const {
    setToast,
    connectedProfile
  } = useAuth();
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const queryClient = useQueryClient();
  const unreadDividerContext = useUnreadDividerOptional();
  let t7;
  if ($[0] !== onCancelReplyQuote) {
    t7 = () => onCancelReplyQuote();
    $[0] = onCancelReplyQuote;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  useKeyPressEvent("Escape", t7);
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropModeOverride, setDropModeOverride] = useState(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState(null);
  const [dismissedQuorumProposalScope, setDismissedQuorumProposalScope] = useState(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t8 = wave.participation.submission_strategy ?? null;
  let t9;
  if ($[2] !== isCurationWave || $[3] !== isMemesWave || $[4] !== isQuorumWave || $[5] !== t8) {
    t9 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t8
    });
    $[2] = isCurationWave;
    $[3] = isMemesWave;
    $[4] = isQuorumWave;
    $[5] = t8;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const resolvedSubmissionExperience = t9;
  const submissionExperience = forceStandardDropComposer ? WaveSubmissionExperience.DEFAULT : resolvedSubmissionExperience;
  const canUseChatComposer = wave.chat.authenticated_user_eligible || privileges.chatRestriction === ChatRestriction.SLOW_MODE;
  let t10;
  if ($[7] !== activeDrop || $[8] !== canUseChatComposer || $[9] !== fixedDropMode || $[10] !== wave.participation.authenticated_user_eligible) {
    t10 = () => {
      if (fixedDropMode === DropMode.CHAT) {
        return false;
      }
      if (fixedDropMode === DropMode.PARTICIPATION) {
        return true;
      }
      if (canUseChatComposer) {
        return false;
      }
      if (wave.participation.authenticated_user_eligible) {
        return true;
      }
      if (activeDrop) {
        return false;
      }
      return false;
    };
    $[7] = activeDrop;
    $[8] = canUseChatComposer;
    $[9] = fixedDropMode;
    $[10] = wave.participation.authenticated_user_eligible;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const getDefaultIsDropMode = t10;
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${canUseChatComposer}:` + `${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeToken = modeScopeKey;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : initialCurationUrlProp;
  const isCurationDropMode = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isDropMode;
  const isQuorumProposalDropMode = submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && isDropMode;
  const quorumProposalScopeKey = `${modeScopeToken}:${submissionExperience}`;
  const isQuorumProposalModalOpen = isQuorumProposalDropMode && dismissedQuorumProposalScope !== quorumProposalScopeKey;
  const canMentionAll = wave.wave.authenticated_user_eligible_for_admin === true;
  const canUseCurationUrlSubmit = fixedDropMode === DropMode.CHAT ? onSubmitCurationUrl !== undefined && canSubmitCurationUrl !== false : true;
  const curationUrlRestrictionMessage = fixedDropMode === DropMode.CHAT && onSubmitCurationUrl !== undefined && canSubmitCurationUrl === false ? curationUrlSubmitRestrictionMessage : null;
  let t11;
  if ($[12] !== canUseChatComposer || $[13] !== fixedDropMode || $[14] !== setToast || $[15] !== wave.participation.authenticated_user_eligible) {
    t11 = newIsDropMode => {
      if (fixedDropMode !== DropMode.BOTH) {
        return false;
      }
      if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to drop in this wave",
          type: "error"
        });
        return false;
      }
      if (!newIsDropMode && !canUseChatComposer) {
        setToast({
          message: "You are not eligible to chat in this wave",
          type: "error"
        });
        return false;
      }
      return true;
    };
    $[12] = canUseChatComposer;
    $[13] = fixedDropMode;
    $[14] = setToast;
    $[15] = wave.participation.authenticated_user_eligible;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const canSwitchDropMode = t11;
  let t12;
  if ($[17] !== canSwitchDropMode || $[18] !== fixedDropMode || $[19] !== modeScopeToken || $[20] !== onExitFixedDropMode) {
    t12 = newIsDropMode_0 => {
      if (!newIsDropMode_0 && fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode) {
        setCurationPrefillSeed(null);
        setDismissedQuorumProposalScope(null);
        onExitFixedDropMode();
        return;
      }
      if (!canSwitchDropMode(newIsDropMode_0)) {
        return;
      }
      setCurationPrefillSeed(null);
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: newIsDropMode_0
      });
    };
    $[17] = canSwitchDropMode;
    $[18] = fixedDropMode;
    $[19] = modeScopeToken;
    $[20] = onExitFixedDropMode;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const onDropModeChange = t12;
  let t13;
  if ($[22] !== canSubmitCurationUrl || $[23] !== canSwitchDropMode || $[24] !== fixedDropMode || $[25] !== modeScopeToken || $[26] !== onSubmitCurationUrl) {
    t13 = url => {
      if (fixedDropMode === DropMode.CHAT) {
        if (onSubmitCurationUrl && canSubmitCurationUrl !== false) {
          onSubmitCurationUrl(url);
        }
        return;
      }
      if (!canSwitchDropMode(true)) {
        return;
      }
      setCurationPrefillSeed({
        scopeKey: modeScopeToken,
        url
      });
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: true
      });
    };
    $[22] = canSubmitCurationUrl;
    $[23] = canSwitchDropMode;
    $[24] = fixedDropMode;
    $[25] = modeScopeToken;
    $[26] = onSubmitCurationUrl;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const onSwitchToDropModeWithUrl = t13;
  let t14;
  if ($[28] !== canUseChatComposer || $[29] !== fixedDropMode || $[30] !== onDropModeChange || $[31] !== onExitFixedDropMode || $[32] !== quorumProposalScopeKey) {
    t14 = () => {
      if (fixedDropMode === DropMode.BOTH && canUseChatComposer) {
        onDropModeChange(false);
        return;
      }
      if (fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode !== undefined) {
        onDropModeChange(false);
        return;
      }
      setDismissedQuorumProposalScope(quorumProposalScopeKey);
    };
    $[28] = canUseChatComposer;
    $[29] = fixedDropMode;
    $[30] = onDropModeChange;
    $[31] = onExitFixedDropMode;
    $[32] = quorumProposalScopeKey;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const onCloseQuorumProposal = t14;
  let t15;
  if ($[34] !== isDropMode || $[35] !== onDropModeChange) {
    t15 = () => {
      setDismissedQuorumProposalScope(null);
      if (!isDropMode) {
        onDropModeChange(true);
      }
    };
    $[34] = isDropMode;
    $[35] = onDropModeChange;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const onOpenQuorumProposal = t15;
  let t16;
  if ($[37] !== canMentionAll) {
    t16 = partIndex => {
      setDrop(prevDrop => {
        if (!prevDrop) {
          return null;
        }
        const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
        return {
          ...prevDrop,
          parts: newParts,
          referenced_nfts: prevDrop.referenced_nfts,
          mentioned_users: prevDrop.mentioned_users,
          mentioned_groups: getMentionedGroupsFromParts(newParts, canMentionAll),
          mentioned_waves: prevDrop.mentioned_waves ?? [],
          metadata: prevDrop.metadata
        };
      });
    };
    $[37] = canMentionAll;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const onRemovePart = t16;
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = new Map();
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const slowModeChatStateByWaveRef = useRef(t17);
  const slowModeChatReservationIdRef = useRef(0);
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = waveId => {
      const currentState = slowModeChatStateByWaveRef.current.get(waveId);
      if (currentState !== undefined) {
        return currentState;
      }
      const nextState = {
        pendingReservationId: null,
        cooldownUntil: null,
        cooldownMs: null
      };
      slowModeChatStateByWaveRef.current.set(waveId, nextState);
      return nextState;
    };
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const getSlowModeChatWaveState = t18;
  let t19;
  if ($[41] !== connectedProfile?.handle || $[42] !== wave.author.handle || $[43] !== wave.chat.slow_mode_cooldown_ms || $[44] !== wave.id || $[45] !== wave.wave.authenticated_user_eligible_for_admin) {
    t19 = dropRequest => {
      if (dropRequest.wave_id !== wave.id) {
        return null;
      }
      const cooldownMs = wave.chat.slow_mode_cooldown_ms;
      const connectedHandle = connectedProfile?.handle?.toLowerCase() ?? null;
      const isCreator = connectedHandle !== null && wave.author.handle?.toLowerCase() === connectedHandle;
      const isAdmin = wave.wave.authenticated_user_eligible_for_admin === true;
      if (typeof cooldownMs !== "number") {
        return null;
      }
      if (dropRequest.drop_type !== ApiDropType.Chat || connectedHandle === null || isCreator || isAdmin) {
        return null;
      }
      return cooldownMs;
    };
    $[41] = connectedProfile?.handle;
    $[42] = wave.author.handle;
    $[43] = wave.chat.slow_mode_cooldown_ms;
    $[44] = wave.id;
    $[45] = wave.wave.authenticated_user_eligible_for_admin;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  connectedProfile?.handle;
  const getLocalSlowModeCooldownMs = t19;
  let t20;
  if ($[47] !== getLocalSlowModeCooldownMs) {
    t20 = dropRequest_0 => {
      const cooldownMs_0 = getLocalSlowModeCooldownMs(dropRequest_0);
      if (cooldownMs_0 === null) {
        return null;
      }
      const waveState = getSlowModeChatWaveState(dropRequest_0.wave_id);
      if (waveState.pendingReservationId !== null) {
        return false;
      }
      const cooldownUntil = waveState.cooldownUntil;
      if (cooldownUntil !== null && Date.now() < cooldownUntil) {
        return false;
      }
      waveState.cooldownUntil = null;
      waveState.cooldownMs = cooldownMs_0;
      slowModeChatReservationIdRef.current = slowModeChatReservationIdRef.current + 1;
      const reservation = {
        id: slowModeChatReservationIdRef.current,
        waveId: dropRequest_0.wave_id,
        cooldownMs: cooldownMs_0
      };
      waveState.pendingReservationId = reservation.id;
      return reservation;
    };
    $[47] = getLocalSlowModeCooldownMs;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const reserveSlowModeChatQueueSlot = t20;
  let t21;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = reservation_0 => {
      if (reservation_0 === null || reservation_0 === undefined) {
        return;
      }
      const waveState_0 = slowModeChatStateByWaveRef.current.get(reservation_0.waveId);
      if (waveState_0 === undefined) {
        return;
      }
      if (waveState_0.pendingReservationId !== reservation_0.id) {
        return;
      }
      waveState_0.pendingReservationId = null;
      waveState_0.cooldownMs = null;
    };
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const clearSlowModeChatPending = t21;
  let t22;
  if ($[50] !== queryClient || $[51] !== wave) {
    t22 = body => {
      const reservation_1 = body.slowModeChatReservation;
      if (reservation_1 === undefined) {
        return;
      }
      const waveState_1 = slowModeChatStateByWaveRef.current.get(reservation_1.waveId);
      if (waveState_1?.pendingReservationId !== reservation_1.id) {
        return;
      }
      const nextDropAllowed = Date.now() + reservation_1.cooldownMs;
      waveState_1.pendingReservationId = null;
      waveState_1.cooldownUntil = nextDropAllowed;
      waveState_1.cooldownMs = reservation_1.cooldownMs;
      queryClient.setQueryData([QueryKey.WAVE, {
        wave_id: reservation_1.waveId
      }], currentWave => {
        const sourceWave = currentWave ?? (wave.id === reservation_1.waveId ? wave : undefined);
        if (sourceWave === undefined) {
          return currentWave;
        }
        return {
          ...sourceWave,
          chat: {
            ...sourceWave.chat,
            next_drop_allowed: nextDropAllowed
          }
        };
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE, {
          wave_id: reservation_1.waveId
        }]
      }).catch(_temp);
    };
    $[50] = queryClient;
    $[51] = wave;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  const startLocalSlowModeCooldown = t22;
  let t23;
  if ($[53] !== curationComposerVariant || $[54] !== processDropRemoved || $[55] !== processIncomingDrop || $[56] !== setToast || $[57] !== startLocalSlowModeCooldown || $[58] !== submissionExperience) {
    t23 = (serverDrop, body_1) => {
      if (body_1.dropId) {
        processDropRemoved(body_1.drop.wave_id, body_1.dropId);
      }
      startLocalSlowModeCooldown(body_1);
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      body_1.onSuccess?.();
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    };
    $[53] = curationComposerVariant;
    $[54] = processDropRemoved;
    $[55] = processIncomingDrop;
    $[56] = setToast;
    $[57] = startLocalSlowModeCooldown;
    $[58] = submissionExperience;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== processDropRemoved || $[61] !== setToast) {
    t24 = (error, body_2) => {
      clearSlowModeChatPending(body_2.slowModeChatReservation);
      setTimeout(() => {
        if (body_2.dropId) {
          processDropRemoved(body_2.drop.wave_id, body_2.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
      body_2.onError?.(error);
    };
    $[60] = processDropRemoved;
    $[61] = setToast;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t23 || $[64] !== t24) {
    t25 = {
      mutationFn: _temp2,
      onSuccess: t23,
      onError: t24,
      retry: _temp3,
      retryDelay: _temp4
    };
    $[63] = t23;
    $[64] = t24;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const addDropMutation = useMutation(t25);
  let t26;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = [];
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  const queueRef = useRef(t26);
  const isProcessingRef = useRef(false);
  const hasBatchErrorsRef = useRef(false);
  const hasServerDropCreatedCallbackErrorsRef = useRef(false);
  const inFlightProcessNextDropRef = useRef(null);
  let t27;
  if ($[67] !== onServerDropCreated) {
    t27 = async serverDrop_0 => {
      if (!onServerDropCreated) {
        return;
      }
      ;
      try {
        await onServerDropCreated(serverDrop_0);
      } catch (t28) {
        const error_0 = t28;
        hasServerDropCreatedCallbackErrorsRef.current = true;
        console.error("Error handling created server drop:", error_0);
      }
    };
    $[67] = onServerDropCreated;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  const handleServerDropCreated = t27;
  let t28;
  if ($[69] !== addDropMutation || $[70] !== handleServerDropCreated || $[71] !== onAllDropsAdded || $[72] !== waitAndInvalidateDrops) {
    t28 = async () => {
      if (isProcessingRef.current || queueRef.current.length === 0) {
        return;
      }
      isProcessingRef.current = true;
      while (queueRef.current.length > 0) {
        const dropRequest_1 = queueRef.current.shift();
        if (dropRequest_1 === undefined) {
          break;
        }
        ;
        try {
          const serverDrop_1 = await addDropMutation.mutateAsync(dropRequest_1);
          await handleServerDropCreated(serverDrop_1);
        } catch (t29) {
          const error_1 = t29;
          hasBatchErrorsRef.current = true;
          console.error("Error processing drop:", error_1);
        }
      }
      isProcessingRef.current = false;
      const shouldNotifyAllDropsAdded = !hasBatchErrorsRef.current && !hasServerDropCreatedCallbackErrorsRef.current;
      hasBatchErrorsRef.current = false;
      hasServerDropCreatedCallbackErrorsRef.current = false;
      waitAndInvalidateDrops();
      if (shouldNotifyAllDropsAdded) {
        onAllDropsAdded?.();
      }
    };
    $[69] = addDropMutation;
    $[70] = handleServerDropCreated;
    $[71] = onAllDropsAdded;
    $[72] = waitAndInvalidateDrops;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  const processNextDrop = t28;
  let t29;
  if ($[74] !== onDropAddedToQueue || $[75] !== processNextDrop || $[76] !== reserveSlowModeChatQueueSlot || $[77] !== unreadDividerContext) {
    t29 = dropRequest_2 => {
      const slowModeChatReservation = reserveSlowModeChatQueueSlot(dropRequest_2.drop);
      if (slowModeChatReservation === false) {
        return false;
      }
      const queuedDropRequest = slowModeChatReservation === null ? dropRequest_2 : {
        ...dropRequest_2,
        slowModeChatReservation
      };
      queueRef.current.push(queuedDropRequest);
      inFlightProcessNextDropRef.current = processNextDrop();
      if (unreadDividerContext) {
        unreadDividerContext.setUnreadDividerSerialNo(null);
      }
      onDropAddedToQueue();
      if (dropRequest_2.drop.drop_type !== ApiDropType.Chat && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      return true;
    };
    $[74] = onDropAddedToQueue;
    $[75] = processNextDrop;
    $[76] = reserveSlowModeChatQueueSlot;
    $[77] = unreadDividerContext;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  const submitDrop = t29;
  const hasExitFixedDropMode = onExitFixedDropMode !== undefined;
  const t30 = fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode ? "Close create drop" : null;
  const t31 = fixedDropMode === DropMode.BOTH && (privileges.chatRestriction === null || privileges.chatRestriction === ChatRestriction.SLOW_MODE) || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  const t32 = privileges.chatRestriction === ChatRestriction.SLOW_MODE;
  let t33;
  if ($[79] !== activeDrop || $[80] !== canUseCurationUrlSubmit || $[81] !== curationUrlRestrictionMessage || $[82] !== drop || $[83] !== dropId || $[84] !== externalAttachmentDrop || $[85] !== identityPickerPlacement || $[86] !== isDropMode || $[87] !== isStormMode || $[88] !== onCancelReplyQuote || $[89] !== onDropModeChange || $[90] !== onExternalAttachmentDropConsumed || $[91] !== onSwitchToDropModeWithUrl || $[92] !== submitDrop || $[93] !== t30 || $[94] !== t31 || $[95] !== t32 || $[96] !== termsSignatureFlowEnabled) {
    t33 = {
      activeDrop,
      onCancelReplyQuote,
      drop,
      isStormMode,
      isDropMode,
      dropId,
      setDrop,
      setIsStormMode,
      onDropModeChange,
      onSwitchToDropModeWithUrl,
      submitDrop,
      dropModeToggleExitLabel: t30,
      canExitDropMode: t31,
      isChatBlockedBySlowMode: t32,
      externalAttachmentDrop,
      onExternalAttachmentDropConsumed,
      canSubmitCurationUrl: canUseCurationUrlSubmit,
      curationUrlSubmitRestrictionMessage: curationUrlRestrictionMessage,
      termsSignatureFlowEnabled,
      identityPickerPlacement
    };
    $[79] = activeDrop;
    $[80] = canUseCurationUrlSubmit;
    $[81] = curationUrlRestrictionMessage;
    $[82] = drop;
    $[83] = dropId;
    $[84] = externalAttachmentDrop;
    $[85] = identityPickerPlacement;
    $[86] = isDropMode;
    $[87] = isStormMode;
    $[88] = onCancelReplyQuote;
    $[89] = onDropModeChange;
    $[90] = onExternalAttachmentDropConsumed;
    $[91] = onSwitchToDropModeWithUrl;
    $[92] = submitDrop;
    $[93] = t30;
    $[94] = t31;
    $[95] = t32;
    $[96] = termsSignatureFlowEnabled;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  const createDropContentProps = t33;
  let dropComposerContent;
  if (isQuorumProposalDropMode) {
    let t34;
    if ($[98] !== activeDrop || $[99] !== dropId || $[100] !== isQuorumProposalModalOpen || $[101] !== onCancelReplyQuote || $[102] !== onCloseQuorumProposal || $[103] !== submitDrop || $[104] !== termsSignatureFlowEnabled || $[105] !== wave) {
      t34 = <QuorumProposalDropModal isOpen={isQuorumProposalModalOpen} activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} submitDrop={submitDrop} onClose={onCloseQuorumProposal} termsSignatureFlowEnabled={termsSignatureFlowEnabled} />;
      $[98] = activeDrop;
      $[99] = dropId;
      $[100] = isQuorumProposalModalOpen;
      $[101] = onCancelReplyQuote;
      $[102] = onCloseQuorumProposal;
      $[103] = submitDrop;
      $[104] = termsSignatureFlowEnabled;
      $[105] = wave;
      $[106] = t34;
    } else {
      t34 = $[106];
    }
    let t35;
    if ($[107] !== isQuorumProposalModalOpen || $[108] !== onOpenQuorumProposal) {
      t35 = !isQuorumProposalModalOpen && <div className="tw-flex tw-w-full tw-justify-end"><button type="button" onClick={onOpenQuorumProposal} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800">Create Proposal</button></div>;
      $[107] = isQuorumProposalModalOpen;
      $[108] = onOpenQuorumProposal;
      $[109] = t35;
    } else {
      t35 = $[109];
    }
    let t36;
    if ($[110] !== t34 || $[111] !== t35) {
      t36 = <>{t34}{t35}</>;
      $[110] = t34;
      $[111] = t35;
      $[112] = t36;
    } else {
      t36 = $[112];
    }
    dropComposerContent = t36;
  } else {
    if (isCurationDropMode) {
      let t34;
      if ($[113] !== activeDrop || $[114] !== curationComposerVariant || $[115] !== dropId || $[116] !== initialCurationUrl || $[117] !== isDropMode || $[118] !== onCancelReplyQuote || $[119] !== submitDrop || $[120] !== termsSignatureFlowEnabled || $[121] !== wave) {
        t34 = <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} termsSignatureFlowEnabled={termsSignatureFlowEnabled} />;
        $[113] = activeDrop;
        $[114] = curationComposerVariant;
        $[115] = dropId;
        $[116] = initialCurationUrl;
        $[117] = isDropMode;
        $[118] = onCancelReplyQuote;
        $[119] = submitDrop;
        $[120] = termsSignatureFlowEnabled;
        $[121] = wave;
        $[122] = t34;
      } else {
        t34 = $[122];
      }
      dropComposerContent = t34;
    } else {
      let t34;
      if ($[123] !== createDropContentProps || $[124] !== submissionExperience || $[125] !== wave) {
        t34 = <CreateDropContent {...createDropContentProps} wave={wave} submissionExperience={submissionExperience} />;
        $[123] = createDropContentProps;
        $[124] = submissionExperience;
        $[125] = wave;
        $[126] = t34;
      } else {
        t34 = $[126];
      }
      dropComposerContent = t34;
    }
  }
  let t34;
  if ($[127] !== drop?.mentioned_groups || $[128] !== drop?.mentioned_users || $[129] !== drop?.mentioned_waves || $[130] !== drop?.parts || $[131] !== drop?.referenced_nfts || $[132] !== isCurationDropMode || $[133] !== isQuorumProposalDropMode || $[134] !== isStormMode || $[135] !== onRemovePart) {
    t34 = isStormMode && !isCurationDropMode && !isQuorumProposalDropMode && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }}><CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedGroups={drop?.mentioned_groups ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} /></motion.div>;
    $[127] = drop?.mentioned_groups;
    $[128] = drop?.mentioned_users;
    $[129] = drop?.mentioned_waves;
    $[130] = drop?.parts;
    $[131] = drop?.referenced_nfts;
    $[132] = isCurationDropMode;
    $[133] = isQuorumProposalDropMode;
    $[134] = isStormMode;
    $[135] = onRemovePart;
    $[136] = t34;
  } else {
    t34 = $[136];
  }
  let t35;
  if ($[137] !== t34) {
    t35 = <AnimatePresence>{t34}</AnimatePresence>;
    $[137] = t34;
    $[138] = t35;
  } else {
    t35 = $[138];
  }
  let t36;
  if ($[139] !== dropComposerContent || $[140] !== t35) {
    t36 = <>{t35}{dropComposerContent}</>;
    $[139] = dropComposerContent;
    $[140] = t35;
    $[141] = t36;
  } else {
    t36 = $[141];
  }
  return t36;
}
function _temp4(failureCount_0) {
  return failureCount_0 * 1000;
}
function _temp3(failureCount) {
  return failureCount < 3;
}
async function _temp2(body_0) {
  return commonApiPost({
    endpoint: "drops",
    body: body_0.drop
  });
}
function _temp() {}